import type { BaseNodeProps } from '../types'

function rolePreviewKey(previews: BaseNodeProps['rolePreviews']): string {
  if (!previews) return ''
  return previews.map((preview) => `${preview.label}:${preview.status}:${preview.url ?? ''}:${preview.message ?? ''}`).join('|')
}

/**
 * Custom comparator to prevent unnecessary re-renders.
 *
 * Props compared:
 * - id (strict equality)
 * - selected (strict equality)
 * - width (strict equality)
 * - height (strict equality)
 * - config (referential equality)
 * - initialData.createdItems (length + item id comparison)
 * - initialData.prompt (strict equality)
 * - initialData.images (referential equality)
 * - orchestratorState.status (strict equality)
 * - initialData.collapsed (strict equality)
 * - initialData.title (strict equality)
 * - initialData.locked (strict equality)
 * - connectedPrompt (strict equality)
 * - connectedImage (strict equality)
 * - error (strict equality)
 * - loading (strict equality)
 * - walletBalance (strict equality)
 * - costProvider (strict equality)
 * - costModel (strict equality)
 * - canvasId (strict equality)
 * - pinnedParameters (referential equality)
 * - pinnedParameterValues (referential equality)
 * - pinnedModelName (strict equality)
 */
export function arePropsEqual(prevProps: BaseNodeProps, nextProps: BaseNodeProps): boolean {
  const prevItems = prevProps.initialData?.createdItems || [];
  const nextItems = nextProps.initialData?.createdItems || [];
  // ⚡ Performance: Shallow comparison instead of JSON.stringify (100ms → <1ms for 100 nodes)
  const createdItemsChanged = prevItems.length !== nextItems.length ||
    prevItems.some((item, idx) => item.id !== nextItems[idx]?.id);

  const orchestratorChanged =
    prevProps.orchestratorState?.status !== nextProps.orchestratorState?.status;

  const collapsedChanged =
    prevProps.initialData?.collapsed !== nextProps.initialData?.collapsed;

  const titleChanged =
    prevProps.initialData?.title !== nextProps.initialData?.title ||
    prevProps.initialData?.titleSource !== nextProps.initialData?.titleSource ||
    prevProps.initialData?.modelName !== nextProps.initialData?.modelName;

  const lockedChanged =
    prevProps.initialData?.locked !== nextProps.initialData?.locked;

  const connectedPromptChanged =
    prevProps.connectedPrompt !== nextProps.connectedPrompt;

  const connectedImageChanged =
    prevProps.connectedImage !== nextProps.connectedImage;

  const rolePreviewsChanged = rolePreviewKey(prevProps.rolePreviews) !== rolePreviewKey(nextProps.rolePreviews)
    || prevProps.roleNote !== nextProps.roleNote;

  const promptChanged =
    prevProps.initialData?.prompt !== nextProps.initialData?.prompt;

  const imagesChanged =
    prevProps.initialData?.images !== nextProps.initialData?.images;

  const pinnedParametersChanged =
    prevProps.pinnedParameters !== nextProps.pinnedParameters;

  const pinnedParameterValuesChanged =
    prevProps.pinnedParameterValues !== nextProps.pinnedParameterValues;

  const pinnedModelNameChanged =
    prevProps.pinnedModelName !== nextProps.pinnedModelName;

  const shouldSkipRender =
    prevProps.id === nextProps.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.config === nextProps.config &&
    prevProps.error === nextProps.error &&
    prevProps.loading === nextProps.loading &&
    prevProps.walletBalance === nextProps.walletBalance &&
    prevProps.costProvider === nextProps.costProvider &&
    prevProps.costModel === nextProps.costModel &&
    prevProps.canvasId === nextProps.canvasId &&
    !createdItemsChanged &&
    !orchestratorChanged &&
    !collapsedChanged &&
    !titleChanged &&
    !lockedChanged &&
    !connectedPromptChanged &&
    !connectedImageChanged &&
    !rolePreviewsChanged &&
    !promptChanged &&
    !imagesChanged &&
    !pinnedParametersChanged &&
    !pinnedParameterValuesChanged &&
    !pinnedModelNameChanged &&
    prevProps.imageExecution === nextProps.imageExecution &&
    prevProps.imageCapability === nextProps.imageCapability &&
    (prevProps.connectedImageUrls ?? []).join('\n') === (nextProps.connectedImageUrls ?? []).join('\n');

  return shouldSkipRender;
}
